import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Network, Loader2, ShieldAlert, KeyRound, PlayCircle, FileSearch, FolderSearch, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NEHEN/TriZetto diagnostics card for the Admin Tools tab.
 * Exposes three admin-only diagnostic endpoints as buttons:
 *   - /api/admin/nehen/test-soap-eligibility (the big one — fires a
 *     canned 270 against the TEST environment and shows the full
 *     response, including WWW-Authenticate + first 500 chars of body)
 *   - /api/admin/nehen/credentials-introspect (masked creds + source)
 *   - /api/admin/nehen/test-connection (SFTP TCP test, POST)
 * Displays the JSON response inline so no console / curl dance.
 */
export const NehenDiagnosticsCard = () => {
  const [loading, setLoading] = useState(null);   // key of the button currently running
  const [result, setResult] = useState(null);
  const [resultLabel, setResultLabel] = useState(null);
  const [pollProgress, setPollProgress] = useState(null); // {steps_done, steps_total, current_step}

  // Run-all-polls is a background job on the backend (the 4 SFTP
  // sub-polls can exceed the ~60s ingress timeout, which used to
  // surface as "Network Error"). We start the job, then poll its
  // status every 3s and render live progress until it finishes.
  const runAllPolls = useCallback(async () => {
    const label = 'Run all SFTP polls (277 + 835 + CSR + 999 force=true)';
    setLoading('run_all_polls');
    setResultLabel(label);
    setResult(null);
    setPollProgress(null);
    try {
      const start = (await axios.post(`${API}/admin/nehen/run-all-polls`)).data;
      if (!start.job_id) {
        // Not configured (missing SFTP creds) — show response as-is
        setResult(start);
        return;
      }
      if (start.already_running) {
        toast.info('A poll run is already in progress — attaching to it');
      }
      const jobId = start.job_id;
      // Poll until completed/failed (safety cap: 10 min)
      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise(res => setTimeout(res, 3000));
        const job = (await axios.get(`${API}/admin/nehen/run-all-polls/${jobId}`)).data;
        setPollProgress({
          steps_done: job.steps_done,
          steps_total: job.steps_total,
          current_step: job.current_step,
        });
        if (job.status === 'completed') {
          setResult(job.result);
          toast.success('All SFTP polls finished');
          return;
        }
        if (job.status === 'failed') {
          setResult({ _error: true, detail: job.error, partial: job.result });
          toast.error(`Poll run failed: ${job.error}`);
          return;
        }
      }
      setResult({ _error: true, detail: 'Timed out waiting for job (still running server-side — check back via logs)' });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setResult({ _error: true, status: err.response?.status, detail });
      toast.error(`${label} failed: ${detail}`);
    } finally {
      setLoading(null);
      setPollProgress(null);
    }
  }, []);

  const run = useCallback(async (key, label, fn) => {
    setLoading(key);
    setResultLabel(label);
    setResult(null);
    try {
      const data = await fn();
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      const status = err.response?.status;
      setResult({
        _error: true,
        status,
        detail,
        hint: status === 403
          ? 'Your user does not have admin role in prod (endpoint requires role = "admin" or "super admin")'
          : status === 404
          ? 'Endpoint not deployed yet — redeploy with the latest code'
          : 'See detail above',
      });
      toast.error(`${label} failed: ${detail}`);
    } finally {
      setLoading(null);
    }
  }, []);

  return (
    <Card className="border-purple-200" data-testid="nehen-diagnostics-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Network className="h-5 w-5 text-purple-600" />
          NEHEN / TriZetto Diagnostics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500">
          Admin-only diagnostics for the TriZetto SOAP + SFTP integration. Output is safe to
          share — credentials are always masked (first 2 + last 2 chars only).
        </p>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-soap-test-btn"
            disabled={loading !== null}
            onClick={() => run(
              'soap_test',
              'SOAP eligibility (TEST env)',
              () => axios
                .post(`${API}/admin/nehen/test-soap-eligibility`, { test_mode: true })
                .then(r => r.data)
            )}
          >
            {loading === 'soap_test'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Running…</>
              : <><PlayCircle className="h-3 w-3 mr-2" />Test SOAP Eligibility (Test Env)</>
            }
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-soap-test-prod-btn"
            disabled={loading !== null}
            onClick={() => run(
              'soap_prod',
              'SOAP eligibility (PROD env)',
              () => axios
                .post(`${API}/admin/nehen/test-soap-eligibility`, { test_mode: false })
                .then(r => r.data)
            )}
          >
            {loading === 'soap_prod'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Running…</>
              : <><ShieldAlert className="h-3 w-3 mr-2" />Test SOAP Eligibility (Prod Env)</>
            }
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-dump-wire-btn"
            disabled={loading !== null}
            onClick={() => run(
              'wire_dump',
              'Wire-body dump (no network call)',
              () => axios
                .post(`${API}/admin/nehen/test-soap-eligibility`, { test_mode: false, dry_run: true })
                .then(r => r.data)
            )}
            title="Build the full SOAP/MTOM request and return the exact bytes that WOULD be sent — without actually contacting TriZetto. Useful for debugging acceptance issues."
          >
            {loading === 'wire_dump'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Building…</>
              : <><FileSearch className="h-3 w-3 mr-2" />Dump Wire Body (Dry Run, No Network Call)</>
            }
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-introspect-btn"
            disabled={loading !== null}
            onClick={() => run(
              'introspect',
              'Credentials introspect',
              () => axios
                .get(`${API}/admin/nehen/credentials-introspect`)
                .then(r => r.data)
            )}
          >
            {loading === 'introspect'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Running…</>
              : <><KeyRound className="h-3 w-3 mr-2" />Show Loaded Credentials (Masked)</>
            }
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-sftp-btn"
            disabled={loading !== null}
            onClick={() => run(
              'sftp',
              'SFTP connection test',
              () => axios
                .post(`${API}/admin/nehen/test-connection`)
                .then(r => r.data)
            )}
          >
            {loading === 'sftp'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Running…</>
              : <><PlayCircle className="h-3 w-3 mr-2" />Test SFTP Connection</>
            }
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-sftp-diagnose-btn"
            disabled={loading !== null}
            onClick={() => run(
              'sftp_diagnose',
              'SFTP folder diagnose (read-only, lists every outbound folder)',
              () => axios
                .get(`${API}/admin/nehen/sftp-diagnose`)
                .then(r => r.data)
            )}
            title="Connect to TriZetto SFTP and list every outbound folder (999, 277, REMITS, CSRREPORTS, eligibility responses) with file counts + sample names. Read-only — nothing is downloaded or removed."
          >
            {loading === 'sftp_diagnose'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Listing folders…</>
              : <><FolderSearch className="h-3 w-3 mr-2" />Diagnose SFTP Folders (No Download)</>
            }
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="nehen-diag-run-all-polls-btn"
            disabled={loading !== null}
            onClick={runAllPolls}
            title="Pull every outstanding 277 / 835 / CSR / 999 file from TriZetto right now. Runs as a background job on the server — no more timeouts on large syncs. Bypasses the 'skip when no batch is pending' short-circuit on the 999 poller, so files already sitting in the outbox are pulled even on a fresh install."
          >
            {loading === 'run_all_polls'
              ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  {pollProgress?.current_step || 'Starting…'}
                  {pollProgress ? ` (${pollProgress.steps_done}/${pollProgress.steps_total})` : ''}
                </>
              : <><Download className="h-3 w-3 mr-2" />Run All SFTP Polls Now</>
            }
          </Button>
          {loading === 'run_all_polls' && (
            <p className="text-[10px] text-gray-500 -mt-1" data-testid="nehen-diag-poll-progress">
              Running as a background job — safe to leave this page; the sync continues server-side.
            </p>
          )}
        </div>

        {result && (
          <div
            data-testid="nehen-diag-result"
            className="border rounded-md bg-gray-50 p-3 mt-2"
          >
            <div className="text-xs font-semibold text-gray-700 mb-1">
              {resultLabel} — result:
            </div>
            <pre className="text-[10px] font-mono whitespace-pre-wrap break-all max-h-96 overflow-y-auto text-gray-800">
              {JSON.stringify(result, null, 2)}
            </pre>
            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(result, null, 2));
                  toast.success('Copied to clipboard');
                }}
                data-testid="nehen-diag-copy-btn"
              >
                Copy JSON
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={() => setResult(null)}
                data-testid="nehen-diag-clear-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NehenDiagnosticsCard;
